import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import cx from "classnames";
import _ from "lodash";
import styles from "./view.module.scss";
import Button from "src/components/button";
import dateFormat from "dateformat";
import moment from "moment";

import { BannerView } from "src/components/banner";
import { LearningRecordCourse } from "src/types/coaching/learners";
import { CourseContext } from "./context";
import { AssessmentCard, LessonCard, PodcastCard, SectionCard, SimulationCard } from "./components/cards";
import { FileService } from "src/utils/file.service";

import { useSearchParams } from "react-router-dom";
import { ContentPlaceholderCard } from "src/pages/coaching/manage/courses/edit/components/cards";
import { getIndexForContent } from "src/pages/coaching/manage/courses/edit/helper";
import pluralize from "pluralize";

import Thumbnail from "src/components/thumbnail";
import { useSelector } from "react-redux";

interface CourseSummaryComponentProps {
  course: LearningRecordCourse;
}

export const CourseSummaryComponent: React.FC<CourseSummaryComponentProps> = ({ course }) => {
  const courseContext = React.useContext(CourseContext);

  const [searchParams] = useSearchParams();

  const contentIndex = useMemo(() => {
    return parseInt(searchParams.get("index") || "0");
  }, [searchParams]);

  const sectionIndex = useMemo(() => {
    return parseInt(searchParams.get("section") || "0");
  }, [searchParams]);

  console.log("Content Index:", contentIndex, "Section Index:", sectionIndex);  

  const name = useMemo(() => _.get(course, "course.name") || "", [course]);
  const status = useMemo(() => _.get(course, "status"), [course]);
  const description = useMemo(() => _.get(course, "course.description") || "", [course]);
  const banner = useMemo(() => _.get(course, "course.banner"), [course]);
  const skills = useMemo(() => _.get(course, "course.skills") || [], [course]);
  const contents = useMemo(() => course?.course?.contents || [], [course]);
  const passPercentage = useMemo(() => _.get(course, "course.passPercentage") || 0, [course]);
  const createdBy = useMemo(()=> _.get(course,"course.publishedBy.icon",null),[course]);
  const firstName = useMemo(()=> _.get(course,"course.publishedBy.firstName",null), [course] );
  const lastName = useMemo(()=> _.get(course,"course.publishedBy.lastName",null), [course] );
  const userProfile = useMemo(()=> _.get(course,"course.publishedBy.icon",null), [course])
  const createdDate = useMemo(() => _.get(course, "course.createdDate") as any,[course])
  const dueDate = _.get(course, "dueDate", null);
  const created = moment(_.get(course, "course.createdDate"));

  const notStarted = _.includes(["NOT_STARTED", "NOT_ENROLLED"], status);


  const { lessonCount, assessmentCount } = useMemo(() => {
    let lessonCount = 0;
    let assessmentCount = 0;

    const lessonEntityTypes = ["lesson", "podcast"];
    const assessmentEntityTypes = ["assessment", "simulation"];

    const countContents = (items: any[]) => {
      items.forEach((item) => {
        if (lessonEntityTypes.includes(item.entityType)) {
          lessonCount++;
        }
        if (assessmentEntityTypes.includes(item.entityType)) {
          assessmentCount++;
        }
        if (item.entityType === "section" && _.size(item.contents) > 0) {
          countContents(item.contents);
        }
      });
    };

    countContents(contents);

    return { lessonCount, assessmentCount };
  }, [contents]);


  const getIndexForLesson = (lesson) => {
    return _.findIndex(contents, (content) => content.uuid === lesson) + 1;
  }

  const handleBackClicked = () => {
    if (courseContext.onBack) {
      courseContext.onBack();
    }
  }

  const handleContentClicked = (index: number, sectionIndex?: number | null) => {
    if(notStarted){
      return;
    }

    if (courseContext.onContentClicked) {
      courseContext.onContentClicked(index);
    }
  }

  const handleStartCourse = () => {
    if (courseContext.startCourse) {
      courseContext.startCourse();
    }
  }

  const handleEnrollCourse = () => {
    if (courseContext.enrollCourse) {
      courseContext.enrollCourse();
    }
  }

  const handleRetakeCourse = () => {
    if(courseContext.retakeCourse) {
      courseContext.retakeCourse();
    }
  }

  return (
    <div className={styles.courseContainer}>
      <div className={styles.left}>
        <div className={styles.courseDetails}>
          {courseContext.onBack && (
            <div className={styles.backButton} onClick={handleBackClicked}>
              <i className="paperflite-left-arrow"></i>
              <div>
                <span className={styles.back}>back</span>{" "}
              </div>
            </div>
          )}

          <div className={styles.details}>
            <div className={styles.name}>{name}</div>
            <div className={styles.thumbnail}>
              <BannerView
                banner={banner as any}
                defaultBanner="/images/assets/courseThumbnail.jpg"
                overlay={false}
              />
            </div>

            <div className={styles.courseMetadata}>
              <div>
              {status === "NOT_ENROLLED" && (
                <Button
                  className={styles.startCourse}
                  text={courseContext.enrollingCourse ? "Enrolling..." : "Enroll Course"}
                  variant="courses-primary"
                  icon="paperflite-book-open"
                  disabled={courseContext.enrollingCourse}
                  onClick={handleEnrollCourse}
                />
              )}

              {status === "NOT_STARTED" && (
                <Button
                  className={styles.startCourse}
                  text={courseContext.startingCourse ? "starting..." : `Start Course ${getDueDate(dueDate!)}`}
                  variant="courses-primary"
                  icon="paperflite-book-open"
                  disabled={courseContext.startingCourse}
                  onClick={handleStartCourse}
                />
              )}

              {status === "IN_PROGRESS" && (
                <Button
                  className={styles.startCourse}
                  text= {`Resume Course ${getDueDate(dueDate!)}`}
                  variant="courses-primary"
                  icon="paperflite-book-open"
                  onClick={() => {
                    if (courseContext.onContentClicked) {
                      courseContext.onContentClicked(0);
                    }
                  }}
                />
              )}

              {status === "COMPLETED" && (
                <Button
                  className={styles.startCourse}
                  text={courseContext.retakingCourse ? "Retaking..." : "Retake Course"}
                  variant="courses-primary"
                  icon="paperflite-reverse"
                  disabled={courseContext.retakingCourse}
                  onClick={handleRetakeCourse}
                />
              )}
              </div>
              <span className={styles.border} />
              <div className={styles.metadatas} >
              <div className={styles.metadata}>
                <i className="paperflite-file" />
                {lessonCount}{" "}
                {pluralize('lesson', lessonCount)}
              </div>
              <div className={styles.metadata}>
                <i className="paperflite-document" />
                {assessmentCount}{" "}
                {pluralize('assessment', assessmentCount)}
              </div>
              <div className={styles.metadata}>
                <i className="paperflite-clock" />
                {FileService.formatDuration(course?.course?.duration || 0, 'long')}
              </div>
              <div className={cx(styles.metadata,styles.profile)}>
                <Thumbnail
                  media={userProfile}
                  defaultImage = "/images/assets/default_asset.jpg"
                  className={styles.icon}
                />
                <span>by {firstName} {lastName}, {getCourseCreated(created)}</span>
              </div>
              </div>
            </div>

            <div className={styles.description}>
              <p className={styles.title}>Description</p>
              <div
                className={styles.descriptionContent}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />
            </div>

            <div className={styles.content}>
              <div className={styles.header}>
                <p className={styles.title}>Course Structure</p>
              </div>
              <div className={styles.contentsList}>
                {contents.map((content, index) => {
                  const type = _.get(content, "entityType");
                  const contentDisplayIndex = getIndexForContent(contents, content.uuid, content.parent);

                  return (
                    <React.Fragment key={content.uuid}>
                      {type === "lesson" && (
                        <LessonCard
                          className={styles.contentCard}
                          lessonIndex={contentDisplayIndex}
                          course={course}
                          lesson={content}
                          index={index}
                          active={index === contentIndex}
                          disabled={notStarted}
                          onClick={handleContentClicked}
                        />
                      )}

                      {type === "assessment" && (
                        <AssessmentCard
                          className={styles.contentCard}
                          course={course}
                          assessmentIndex={contentDisplayIndex}
                          assessment={content}
                          index={index}
                          active={index === contentIndex}
                          disabled={notStarted}
                          onClick={handleContentClicked}
                        />
                      )}

                      {type === "section" && (
                        <SectionCard
                          className={styles.contentCard}
                          course={course}
                          section={content}
                          sectionIndex ={contentDisplayIndex}
                          index={index}
                          active={index === contentIndex}
                          disabled={notStarted}
                          onClick={handleContentClicked}
                        />
                      )}

                      {type === "podcast" && (
                        <PodcastCard
                          className={styles.contentCard}
                          podcastIndex={contentDisplayIndex}
                          course={course}
                          podcast={content}
                          index={index}
                          active={index === contentIndex}
                          disabled={notStarted}
                          onClick={handleContentClicked}
                        />
                      )}

                      {type === "simulation" && (
                        <SimulationCard
                          className={styles.contentCard}
                          simulationIndex={contentDisplayIndex}
                          course={course}
                          simulation={content}
                          index={index}
                          active={index === contentIndex}
                          disabled={notStarted}
                          onClick={handleContentClicked}
                        />
                      )}
                      
                      <div className={styles.dividerLine}></div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        <p>Skills you will get</p>
        <div className={styles.skills}>
          {_.size(skills) === 0 && (
            <div className={styles.empty}>No skills available</div>
          )}

          {skills.map((skill: any) => {
            return (
              <div key={skill.id} className={styles.skill}>
                <div className={styles.name}>{skill.name}</div>
              </div>
            );
          })}
        </div>
        <div className={styles.passPercentage}>
          <p className={styles.heading}>Pass percentage to get:</p>
          <p className={styles.percentage}>{passPercentage}%</p>
          <p className={styles.description}>
            This represents the minimum pass percentage that you must 
            achieve for this course.
          </p>
        </div>
      </div>
    </div>
  );
};

export const getDueText = (dueDate, status) => {
  if (!dueDate) return "";

  const today = moment().startOf("day");
  const due = moment(dueDate).startOf("day");
  const diff = due.diff(today, "days");

  if (diff > 0) return `Due in ${diff} ${pluralize("day", diff)}`;
  if (diff === 0) return "Due today";

  if (status === "COMPLETED") {
    return `late by ${Math.abs(diff)} ${pluralize("day", Math.abs(diff))}`;
  }

  return `overdue by ${Math.abs(diff)} ${pluralize("day", Math.abs(diff))}`;
};

export const LoadingCoursePage: React.FC<object> = () => {
  return (
    <div className={cx(styles.loadingContainer, styles.placeholder)}>
      <div className={cx(styles.name, styles.block)}></div>
      <div className={cx(styles.thumbnail, styles.block)}></div>

      <div className={cx(styles.description)}>
        <div className={cx(styles.block, styles.label)}></div>
        <div className={cx(styles.block, styles.input)}></div>
        <div className={cx(styles.block, styles.input)}></div>
      </div>

      <div className={styles.content}>
        <div className={cx(styles.header, styles.block)}></div>
        <div className={styles.contentsList}>
          <ContentPlaceholderCard />
        </div>
      </div>
    </div>)
}

export const getDueDate = (dueDate: number) => {
    if (!dueDate) return ""; 

    const date = moment(dueDate).startOf("day");
    const today = moment().startOf("day");
    const diff = date.diff(today, "days");

    if (diff > 0) {
      return `/ ${diff} ${pluralize("day", diff)} left`;
    }

    if (diff === 0){
      return `/ today`;
    }

    // diff < 0 → overdue
    const abs = Math.abs(diff);
    if (diff < 0) {
      return (
         `/ overdue by ${abs} ${pluralize("day", abs)}`
      );
    }
    // For >1 week
    const weeks = Math.floor(abs / 7);
    return (
      `/ overdue by ${weeks} ${pluralize("week", weeks)}`
    );
  };

  export const getCourseCreated = (created) => {
   const today = moment().startOf("day");
   const createdDate = moment(created).startOf("day");
   const diffInDays = today.diff(createdDate, "days");

   if (diffInDays === 0) return "today";

   if (diffInDays <= 7) return `${diffInDays} ${pluralize("day", diffInDays)} ago`;

   const weeks = Math.floor(diffInDays / 7);
   if (diffInDays > 7) return `${weeks} ${pluralize("week",weeks)} ago`;
   return created.format("MMM D, YYYY");
  };